/**
 * Орлогын графикийн өгөгдөл + шинэ борлуулалтын optimistic нэмэлт.
 *
 * ⚠️ Шинэ борлуулалт ирэхэд БҮТЭН татац хийхгүй — тухайн bucket дээр
 *    дүнг нэмээд react-query-ийн cache-ыг шууд засна. Бүтэн татац нь
 *    зөвхөн: муж солих · холболт сэргэх · таб буцаж идэвхжих үед.
 *
 * ⚠️ Мөнгө string. Нэмэхдээ `Number` руу хөрвүүлж буцааж string
 *    болгоно — MNT-д аравтын орон байхгүй тул алдагдах зүйлгүй
 *    (`Number.MAX_SAFE_INTEGER` = 9×10¹⁵ ≈ 9 их наяд төгрөг).
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { revenueBucketKey, type RevenueRange } from '@boots/domain';
import { api, type Envelope } from './api';
import type { RevenueReport } from './types';

export const revenueKey = (range: RevenueRange) => ['revenue', range] as const;

export function useRevenueData(range: RevenueRange) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: revenueKey(range),
    queryFn: () => api<Envelope<RevenueReport>>(`/reports/revenue?range=${range}`),
    // Урсгал ажиллаж байгаа тул тогтмол дахин татах шаардлагагүй.
    // Урсгал тасарвал `use-revenue-stream` polling руу шилжинэ.
    staleTime: 5 * 60_000,
  });

  /** Шинэ борлуулалтыг зөв баганад нэмнэ. Дахин татахгүй. */
  const applySale = useCallback(
    (amountMnt: string, occurredAt: string) => {
      const key = revenueBucketKey(range, new Date(occurredAt));

      qc.setQueryData<Envelope<RevenueReport>>(revenueKey(range), (prev) => {
        if (!prev) return prev;
        const r = prev.data;

        const i = r.points.findIndex((p) => p.bucket === key);
        // Харагдаж буй цонхны гадна унасан бол хөндөхгүй — дараагийн
        // бүтэн татацад орно
        if (i === -1) return prev;

        const points = r.points.map((p, idx) =>
          idx === i
            ? {
                ...p,
                revenueMnt: (Number(p.revenueMnt) + Number(amountMnt)).toFixed(2),
                orderCount: p.orderCount + 1,
              }
            : p,
        );

        // ⚠️ Өрсөлдөөний эрэмбэ мөн хуучирлаа. Шинэ SSE холболт
        //    НЭЭХГҮЙ — нэг урсгал хоёр блокыг тэжээнэ. Эрэмбийг
        //    optimistic-оор тооцох боломжгүй (нормчлол нь БҮХ
        //    субьектээс хамаарна) тул дахин татахаас өөр зам алга.
        void qc.invalidateQueries({ queryKey: ['leaderboard'] });

        const total = (Number(r.total) + Number(amountMnt)).toFixed(2);
        const prevTotal = Number(r.previousTotal);

        return {
          ...prev,
          data: {
            ...r,
            points,
            total,
            orderCount: r.orderCount + 1,
            changePercent:
              prevTotal === 0
                ? null
                : Math.round(((Number(total) - prevTotal) / prevTotal) * 1000) / 10,
          },
        };
      });
    },
    [qc, range],
  );

  /** Холболт сэргэх / таб идэвхжихэд — зөрүү үлдэхгүй байх баталгаа. */
  const refetchAll = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['revenue'] });
  }, [qc]);

  return { ...query, applySale, refetchAll };
}
